import {
	Dispatch,
	FC,
	SetStateAction,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { ProductType } from '@/remoteApi/ProductTypeApi';
import {
	Box,
	Button,
	Divider,
	Flex,
	NumberInput,
	Select,
	SimpleGrid,
	Text,
	Title,
	Tooltip,
} from '@mantine/core';
import { FormOptions, QuantityPerDimensionForm } from './ProductTypeForm';
import { SlPlus, SlTrash } from 'react-icons/sl';
import { calculateMinCustomDimensionProducts } from '@/utilities';

interface Props {
	withCustomDimensions: boolean;
	productType: ProductType;
	quantityPerDimension: QuantityPerDimensionForm;
	setQuantityPerDimension: Dispatch<SetStateAction<QuantityPerDimensionForm>>;
	isValidating: boolean;
	formOptions: FormOptions;
}

export const QuestionDimension: FC<Props> = ({
	withCustomDimensions,
	productType,
	quantityPerDimension,
	setQuantityPerDimension,
	isValidating,
	formOptions,
}) => {
	const initialPredefinedDimensions = {
		dimensions: '',
		min: 10,
		quantity: 100,
	};

	const [customDimensions, setCustomDimensions] = useState({
		height: 100,
		width: 100,
		depth: 100,
		quantity: productType.minCustomDimensionQuantity,
	});

	const [predefinedDimensions, setPredefinedDimensions] = useState(
		initialPredefinedDimensions
	);

	const options = productType.predefinedDimensions.map((dimesions, idx) => ({
		value: idx.toString(),
		label: `${dimesions.depth}x${dimesions.width}x${dimesions.height}`,
	}));

	const calculatedMinQuantity = useMemo(() => {
		if (!formOptions.minTotalSurface)
			return productType.minCustomDimensionQuantity;

		return calculateMinCustomDimensionProducts(
			customDimensions.width,
			customDimensions.height,
			customDimensions.depth,
			formOptions.minTotalSurface
		);
	}, [
		formOptions.minTotalSurface,
		customDimensions.width,
		customDimensions.height,
		customDimensions.depth,
	]);

	const calculatedMinQuantityForPredefined = useMemo(() => {
		if (!formOptions.minTotalSurface)
			return productType.minCustomDimensionQuantity;

		const [depth, width, height] = options[
			Number(predefinedDimensions.dimensions)
		].label
			.split('x')
			.map(Number);

		return calculateMinCustomDimensionProducts(
			width,
			height,
			depth,
			formOptions.minTotalSurface
		);
	}, [formOptions.minTotalSurface, predefinedDimensions]);

	useEffect(() => {
		setCustomDimensions((prev) => ({
			...prev,
			quantity: calculatedMinQuantity,
		}));
	}, [calculatedMinQuantity]);

	useEffect(() => {
		setPredefinedDimensions((prev) => ({
			...prev,
			quantity: calculatedMinQuantityForPredefined,
			min: calculatedMinQuantityForPredefined,
		}));
	}, [calculatedMinQuantityForPredefined]);

	const handleCustomDimensionsChange = (
		key: keyof typeof customDimensions,
		value: (typeof customDimensions)[keyof typeof customDimensions]
	) => {
		setCustomDimensions((prev) => ({ ...prev, [key]: value }));
	};

	const handleAddDimension = (type: 'custom' | 'predifined') => () => {
		switch (type) {
			case 'custom':
				setQuantityPerDimension((prev) => [
					...prev,
					{
						height: customDimensions.height,
						width: customDimensions.width,
						depth: customDimensions.depth,
						quantity: customDimensions.quantity,
						price: 0,
						isCustom: true,
					},
				]);
				setCustomDimensions({
					height: 100,
					width: 100,
					depth: 100,
					quantity: 10,
				});
				break;
			case 'predifined':
				const [depth, width, height] = options[
					Number(predefinedDimensions.dimensions)
				].label
					.split('x')
					.map(Number);

				setQuantityPerDimension((prev) => [
					...prev,
					{
						height,
						width,
						depth,
						quantity: predefinedDimensions.quantity,
						isCustom: false,
						price: 0,
					},
				]);
				break;
		}
	};

	const handleRemoveDimension = (idx: number) => () => {
		setQuantityPerDimension((prev) => prev.filter((_, i) => i !== idx));
	};

	const isError = isValidating && !quantityPerDimension.length;

	return (
		<Box>
			{withCustomDimensions && (
				<>
					<Title order={4} c="black" mb="md">
						Wpisz własny rozmiar dla wyceny
					</Title>
					<SimpleGrid
						cols={{ base: 1, lg: 4, xl: 5 }}
						mb="lg"
						style={{
							alignItems: 'flex-end',
						}}
					>
						<NumberInput
							error={isError}
							label="Długość"
							placeholder="Wpisz długość"
							min={50}
							step={1}
							suffix=" mm"
							value={customDimensions.depth}
							onChange={(val) =>
								handleCustomDimensionsChange('depth', Number(val))
							}
						/>
						<NumberInput
							error={isError}
							label="Szerokość"
							placeholder="Wpisz szerokość"
							min={50}
							step={1}
							name="width"
							suffix=" mm"
							value={customDimensions.width}
							onChange={(val) =>
								handleCustomDimensionsChange('width', Number(val))
							}
						/>
						<NumberInput
							error={isError}
							label="Wysokość"
							placeholder="Wpisz wysokość"
							min={50}
							step={1}
							suffix=" mm"
							value={customDimensions.height}
							onChange={(val) =>
								handleCustomDimensionsChange('height', Number(val))
							}
						/>
						<NumberInput
							error={isError}
							label="Liczba sztuk"
							placeholder="Wpisz liczbę"
							min={calculatedMinQuantity}
							step={1}
							suffix=" szt."
							value={customDimensions.quantity}
							onChange={(val) =>
								handleCustomDimensionsChange('quantity', Number(val))
							}
						/>
						<Tooltip
							label="Dodaj co najmniej jeden rozmiar produktu!"
							opened={isError}
							color="red"
							disabled={!isError}
						>
							<Button
								color="blue.8"
								onClick={handleAddDimension('custom')}
								disabled={customDimensions.quantity < calculatedMinQuantity}
							>
								<Flex gap="sm">
									<SlPlus size={16} />
									<Text fw="bold">Dodaj rozmiar</Text>
								</Flex>
							</Button>
						</Tooltip>
					</SimpleGrid>
				</>
			)}
			<SimpleGrid
				cols={{ base: 1, lg: 3 }}
				style={{
					alignItems: 'flex-end',
				}}
			>
				<Select
					error={isError}
					label={
						withCustomDimensions
							? 'Lub wybierz rozmiar z gotowej listy'
							: 'Wybierz rozmiar z gotowej listy'
					}
					placeholder="Wybierz rozmiar"
					data={options}
					value={predefinedDimensions.dimensions || ''}
					onChange={(val) => {
						if (!val) return;

						setPredefinedDimensions((prev) => ({
							...prev,
							dimensions: val,
						}));
					}}
				/>
				<NumberInput
					error={isError}
					label="Liczba sztuk"
					placeholder="Wpisz liczbę"
					min={calculatedMinQuantityForPredefined}
					step={1}
					suffix=" szt."
					value={predefinedDimensions.quantity}
					onChange={(val) => {
						setPredefinedDimensions((prev) => ({
							...prev,
							quantity: Number(val),
						}));
					}}
				/>
				<Tooltip
					label="Dodaj co najmniej jeden rozmiar produktu!"
					opened={isError}
					color="red"
					disabled={!isError}
				>
					<Button
						color="blue.8"
						onClick={handleAddDimension('predifined')}
						disabled={!predefinedDimensions.dimensions}
					>
						<Flex gap="sm">
							<SlPlus size={16} />
							<Text fw="bold">Dodaj rozmiar</Text>
						</Flex>
					</Button>
				</Tooltip>
			</SimpleGrid>
			{!!quantityPerDimension.length && (
				<Box>
					<Divider my="lg" />
					<Box>
						<Title order={4} c="black">
							Wybrane rozmiary
						</Title>
						{quantityPerDimension.map((dimensions, idx) => (
							<SimpleGrid
								key={idx}
								cols={{
									base: 1,
									lg: 5,
								}}
								mt="md"
								style={{
									alignItems: 'flex-end',
								}}
							>
								<NumberInput
									readOnly
									label="Długość"
									suffix=" mm"
									value={dimensions.depth}
								/>
								<NumberInput
									readOnly
									label="Szerokość"
									suffix=" mm"
									value={dimensions.width}
								/>
								<NumberInput
									readOnly
									label="Wysokość"
									suffix=" mm"
									value={dimensions.height}
								/>
								<NumberInput
									readOnly
									label="Liczba sztuk"
									suffix=" szt."
									value={dimensions.quantity}
								/>
								<Button onClick={handleRemoveDimension(idx)}>
									<Flex gap="sm">
										<SlTrash size={16} />
										<Text fw="bold">Usuń</Text>
									</Flex>
								</Button>
							</SimpleGrid>
						))}
					</Box>
				</Box>
			)}
		</Box>
	);
};
